import {
  authGet,
  authDelete,
  authPost,
  authPut,
  authGetText,
  authRequestRaw,
} from "./screens/auth/auth";
import {
  Finding,
  DeploymentRepoResponse,
  DeploymentRepoListResponse,
  DeploymentScansResponse,
  EmailSignupBody,
  EmailSignupResponse,
  RepoChanges,
  RunBody,
  RunResponse,
  Snippet,
  SubmitDeploymentRepoResponse,
  AppConfig,
  Repo,
  AuthProvider,
  DeploymentReposRefreshResponse,
  RepoInfo,
  ReleaseInfo,
  DeploymentGithubAppStatusResponse,
  SubscriptionSignupBody,
  SubscriptionSignupResponse,
  FetchGitlabGroupsSelectionsResponse,
  UpdateGitlabGroupsSelectionsRequest,
  GitlabGroup,
  UpdateFindingBody,
  UserSurveyBody,
  FindingCounts,
  DeploymentRepoConfigResponse,
  OrgData,
  UserPermissionsResponse,
  FetchFindingsResponse,
  FindingFilterItems,
} from "./types";
import { checkStatus } from "./fetch";
import parseLinkHeader from "parse-link-header";
import { ago, SECOND } from "./util";
import { TimeLookbackRange } from "./screens/deployment/overview/types";
import { calculateLookbackSeconds } from "./screens/deployment/overview/api";

export const AUTH_URL = `/api/auth`;
const RUN_URL = `/api/run`;
export const DEPLOYMENT_URL = `/api/agent/deployments`;
const EMAIL_SIGNUP = "/api/mailing-list-signup";
const TEMPLATE_URL = "/api/templates";
const GITHUB_API = "https://api.github.com/repos";

function requestTextRaw(method: string, path: string): Promise<Response> {
  const headers = {
    Accept: "application/text",
  };

  return fetch(path, {
    method: method,
    headers,
  });
}

export const fetchAnyUrlText = (url: string): Promise<string> => {
  return requestTextRaw("GET", url).then((r) => {
    return r.text();
  });
};

function requestJsonRaw(method: string, path: string): Promise<Response> {
  const headers = {
    Accept: "application/json",
  };

  return fetch(path, {
    method: method,
    headers,
  });
}

export const fetchRepoInfo = (org: string, repo: string): Promise<RepoInfo> => {
  return requestJsonRaw("GET", `${GITHUB_API}/${org}/${repo}`).then((r) => {
    return r.json() as unknown as RepoInfo;
  });
};

export const fetchLatestReleaseInfo = (
  org: string,
  repo: string
): Promise<ReleaseInfo> => {
  return requestJsonRaw(
    "GET",
    `${GITHUB_API}/${org}/${repo}/releases/latest`
  ).then((r) => {
    return r.json() as unknown as ReleaseInfo;
  });
};

export const fetchAppConfig = (): Promise<AppConfig> => {
  return authGet<AppConfig>(`${AUTH_URL}/config`);
};

export const fetchAuthConfig = (
  authProviderId: string
): Promise<AuthProvider> => {
  return authGet<AuthProvider>(`${AUTH_URL}/config/${authProviderId}`);
};

export const fetchSsoProvider = (ssoEmail: string): Promise<AuthProvider> => {
  return authGet<AuthProvider>(
    `${AUTH_URL}/sso?email=${encodeURIComponent(ssoEmail)}`
  );
};

export const refreshIdentity = (): Promise<{ token: string }> =>
  authPut<{}, { token: string }>(`${AUTH_URL}/refresh`, {});

interface EmailSettingsBody {
  email: string;
  subscribe: boolean;
  contact_about_experiments: boolean;
  used_cli_login: boolean;
}

export const putUserEmail = (
  email: string,
  subscribe: boolean,
  contact_about_experiments: boolean,
  used_cli_login: boolean
) => {
  return authPut<EmailSettingsBody, string>("/api/user/email", {
    email,
    subscribe,
    contact_about_experiments,
    used_cli_login,
  });
};

export const run = (
  snippet: Snippet,
  yaml: boolean,
  version?: string,
  deep: boolean = false
): Promise<RunResponse> => {
  return authPost<RunBody, RunResponse>(RUN_URL, {
    ...snippet,
    version,
    yaml,
    deep,
  });
};

export const fetchCodeTemplate = (
  language: string,
  filename: string
): Promise<string> => {
  return authGetText(`${TEMPLATE_URL}/${language}/${filename}`);
};

export const fetchDeploymentGithubAppStatus = (
  deploymentId: string
): Promise<DeploymentGithubAppStatusResponse> => {
  return authGet<DeploymentGithubAppStatusResponse>(
    `${DEPLOYMENT_URL}/${deploymentId}/github_app_status`
  );
};

export const fetchDeploymentRepos = (
  deploymentId: string
): Promise<DeploymentRepoListResponse> => {
  let url = `${DEPLOYMENT_URL}/${deploymentId}/repos`;
  return authGet<DeploymentRepoListResponse>(url);
};

export const fetchDeploymentRepo = (
  deploymentId: string,
  repoPath: string
): Promise<DeploymentRepoResponse> => {
  let url = `${DEPLOYMENT_URL}/${deploymentId}/repos/${repoPath}`;
  return authGet<DeploymentRepoResponse>(url);
};

export const submitDeploymentRepo = (
  deploymentId: string,
  repo: Repo,
  repoChanges: RepoChanges
) => {
  return authPut<RepoChanges, SubmitDeploymentRepoResponse>(
    `${DEPLOYMENT_URL}/${deploymentId}/repos/${repo.name}`,
    { ...repo, ...repoChanges }
  );
};

export const deleteDeploymentRepo = (
  deploymentId: string,
  repoPath: string
): Promise<string> => {
  const path = `${DEPLOYMENT_URL}/${deploymentId}/repos/${repoPath}`;
  return authDelete<string>(path);
};

export const refreshDeploymentRepos = (
  deploymentId: string
): Promise<DeploymentReposRefreshResponse> => {
  let url = `${DEPLOYMENT_URL}/${deploymentId}/repos/_refresh`;
  return authPost<{}, DeploymentReposRefreshResponse>(url, {});
};

export const fetchDeploymentRepoConfig = (
  deploymentId: string,
  repoPath: string
): Promise<DeploymentRepoConfigResponse> => {
  const url = `${DEPLOYMENT_URL}/${deploymentId}/repos/${repoPath}/_config`;
  return authGet<DeploymentRepoConfigResponse>(url);
};

export const fetchDeploymentScans = (
  deploymentId: string
): Promise<DeploymentScansResponse> => {
  return authGet<DeploymentScansResponse>(
    `${DEPLOYMENT_URL}/${deploymentId}/scans`
  );
};

const _getFindingsParamsFromSearch = (
  search: URLSearchParams
): URLSearchParams => {
  // Remove params from search that are only relevant to frontend
  const filterQuery = new URLSearchParams(search);

  // The following params are only relevant for the frontend
  filterQuery.delete("policy_name");

  // The following params we may want to add back later (when we can filter by ref type)
  filterQuery.delete("ref_type");

  return filterQuery;
};

const _fetchFindings =
  (path: string) =>
  async (
    org: OrgData,
    url: string | undefined,
    searchParams?: URLSearchParams
  ): Promise<FetchFindingsResponse> => {
    // This function requests findings in batches, to prevent OOM for
    // deployments with exceptionally high numbers of findings

    let queryParams = new URLSearchParams();
    if (searchParams) {
      // Clean up search params
      queryParams = _getFindingsParamsFromSearch(searchParams);
    }

    // add "since" param
    if (path === "issues") {
      if (searchParams?.get("since") === "") {
        const since = (Date.now() - ago(org)) / SECOND;
        queryParams.append("since", since.toString());
      }
    }

    if (url === undefined) {
      const qs = queryParams.toString();
      url = `${DEPLOYMENT_URL}/${org.id}/${path}`;
      if (qs !== "") {
        url += `?${qs}`;
      }
    }
    // Uses response headers to request next chunk of data
    const header = { "X-FeatureFlags": "true" } as Record<string, string>;
    const response: Response = await authRequestRaw(
      "get",
      url,
      undefined,
      header
    );
    checkStatus(response);
    const linkHeader = response.headers.get("Link");
    const parsedLinks = linkHeader ? parseLinkHeader(linkHeader) : null;
    const response_body = await response.json();
    const fetchedFindings = response_body.issues as Finding[];
    const fetchedCounts = response_body.counts as FindingCounts;
    const fetchedFilterItems = response_body.filters as FindingFilterItems;
    return {
      fetchedFindings,
      fetchedCounts,
      fetchedNextUrl: parsedLinks?.["next"]?.url,
      fetchedFilterItems,
    };
  };

export const fetchDeploymentFindings = _fetchFindings("issues");
export const fetchDeploymentFindingsPaginated = (
  org: OrgData,
  searchParams: URLSearchParams,
  cursor?: string
): Promise<FetchFindingsResponse> => {
  if (cursor) {
    searchParams.append("cursor", cursor);
  }
  return _fetchFindings("issues/v2")(org, undefined, searchParams);
};

export const fetchDeploymentInventoryFindings =
  _fetchFindings("inventory_issues");

export const fetchDeploymentFilteredFindings = (
  org: OrgData,
  searchParams: URLSearchParams,
  unfilteredFindings?: Finding[],
  unfilteredNextUrl?: string,
  unfilteredFilterItems?: FindingFilterItems,
  cursor?: string
): Promise<FetchFindingsResponse> => {
  if (searchParams.toString() === "") {
    // use unfiltered findings
    return Promise.resolve({
      fetchedFindings: unfilteredFindings || [],
      fetchedNextUrl: unfilteredNextUrl,
      fetchedFilterItems: unfilteredFilterItems || ({} as FindingFilterItems),
    });
  } else if (cursor) {
    searchParams.append("cursor", cursor);
  }
  return fetchDeploymentFindings(org, undefined, searchParams);
};

export const updateDeploymentFindingsBulk = (
  deploymentId: string,
  searchParams: URLSearchParams,
  timeLookbackRange: TimeLookbackRange,
  updates: UpdateFindingBody
) => {
  let queryParams = new URLSearchParams();
  if (searchParams) {
    // Clean up search params
    queryParams = _getFindingsParamsFromSearch(searchParams);
  }
  queryParams.set(
    "since",
    calculateLookbackSeconds(timeLookbackRange).toString()
  );
  if (queryParams.get("tab") === null) {
    queryParams.set("tab", "open");
  }

  return authPut<UpdateFindingBody, Finding[]>(
    `${DEPLOYMENT_URL}/${deploymentId}/issues?${queryParams.toString()}`,
    updates
  );
};

export const updateDeploymentFinding = (
  deploymentId: string,
  findingId: number,
  update: UpdateFindingBody
) => {
  return authPut<UpdateFindingBody, Finding>(
    `${DEPLOYMENT_URL}/${deploymentId}/issues/${findingId}`,
    update
  );
};

export const mailingListSignup = (email: string) => {
  return authPost<EmailSignupBody, EmailSignupResponse>(EMAIL_SIGNUP, {
    email,
  });
};

export const subscriptionSignup = (
  deploymentId: string,
  priceId: string,
  qty: number
) => {
  const url = `/api/payments/subscriptions`;
  return authPost<SubscriptionSignupBody, SubscriptionSignupResponse>(url, {
    deployment_id: deploymentId,
    price_id: priceId,
    quantity: qty,
  });
};

export const fetchGitlabGroupsSelections =
  (): Promise<FetchGitlabGroupsSelectionsResponse> => {
    return authGet<FetchGitlabGroupsSelectionsResponse>(
      `/api/gitlab/organizations`
    );
  };

export const updateGitlabGroupsSelections = (
  enableOrganizations: GitlabGroup[]
): Promise<FetchGitlabGroupsSelectionsResponse> => {
  const url = `/api/gitlab/organizations`;
  return authPut<
    UpdateGitlabGroupsSelectionsRequest,
    FetchGitlabGroupsSelectionsResponse
  >(url, {
    organizations: enableOrganizations,
  });
};

export const createUserSurveyResponse = (userSurvey: UserSurveyBody) => {
  const url = `/api/users/survey`;
  return authPost<UserSurveyBody, {}>(url, userSurvey);
};

export const createUserAppTourFinished = (hasUserFinishedAppTour: boolean) => {
  const url = `/api/users/app_tour`;
  return authPost<{ has_finished_tour: boolean }, {}>(url, {
    has_finished_tour: hasUserFinishedAppTour,
  });
};

export const fetchFindingCounts = (
  deploymentId: string
): Promise<FindingCounts> => {
  return authGet<FindingCounts>(
    `${DEPLOYMENT_URL}/${deploymentId}/issue_counts`
  );
};

export const fetchUserPermissions = (
  deploymentId: string
): Promise<UserPermissionsResponse> => {
  return authGet<UserPermissionsResponse>(
    `/api/users/deployments/${deploymentId}/authorized_actions`
  );
};

export const postIssueSentimentFeedback = (
  feedback: string,
  deploymentId: string,
  issue_hash: string,
  sentiment: string,
  issueId: string
) => {
  return authPost<{ feedback: string; hash: string; sentiment: string }, {}>(
    `/api/agent/deployments/${deploymentId}/issues/${issueId}/sentiment/feedback`,
    {
      feedback,
      sentiment,
      hash: issue_hash,
    }
  );
};
